import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useParams } from "react-router-dom";
import Actions from "../../../components/controls/Actions";
import { DialogOptions, SearchDialog } from "../../../components/dialogs/SearchDialog";
import Errors, { getString } from "../../../components/errors/Errors";
import CreatedModifiedInfo from "../../../components/forms/CreatedModifiedInfo";
import FormData from "../../../components/forms/FormData";
import FormLabel from "../../../components/forms/FormLabel";
import FormTable from "../../../components/forms/FormTable";
import FormTr from "../../../components/forms/FormTr";
import { DefaultPagination, getPagination, Pagination } from "../../../components/listview/Pagination";
import ReferencedItemsView from "../../../components/listview/ReferencedItemsView";
import Title from "../../../components/Title";
import useAbortController from "../../../hooks/useAbortController";
import useGlobalState from "../../../hooks/useGlobalState";
import useStatusBarState from "../../../hooks/useStatusBar";
import { Administration } from "../../../listsSettings/administration";
import { ErrorCode, IAction, SearchResult, UserPermissions } from "../../../services/types";
import {
  formatBoolean,
  getCloseAction,
  getCurrentUrlAsSource,
  getSelectedItems,
  getSourceFromUrl,
} from "../../../services/utils";
import { getUserPermissions } from "../../../services/webapi";
import strings from "../../../strings";
import Add from "../../../svg/Add.svg?react";
import Bin from "../../../svg/Bin.svg?react";
import Edit from "../../../svg/Edit.svg?react";
import * as GroupFields from "../groups/Fields";
import { GroupFull } from "../groups/types";
import { editForm as editFormGroup } from "../groups/urls";
import { searchGroups } from "../groups/webapi";
import * as UserFields from "../users/Fields";
import { UserFull } from "../users/types";
import { searchUsers } from "../users/webapi";
import * as Fields from "./Fields";
import { GlobalNavigationItemFull } from "./types";
import { allItems, editForm } from "./urls";
import {
  addGroup,
  addUser,
  deleteGlobalNavigationItem,
  getGlobalNavigationItem,
  getGlobalNavigationItemGroups,
  getGlobalNavigationItemGroupsFilters,
  getGlobalNavigationItemUsers,
  getGlobalNavigationItemUsersFilters,
  removeGroups,
  removeUsers,
} from "./webapi";

const DispForm: FC = () => {
  const setPage = useGlobalState((state) => state.setPage);
  const setListName = useGlobalState((state) => state.setListName);
  const [abortController, resetAbortController] = useAbortController();
  const [state, setState] = useState<GlobalNavigationItemFull>();
  const [errorCode, setErrorCode] = useState<ErrorCode>();
  const [permissions, setPermissions] = useState<UserPermissions>();
  const [userPermissions, setUserPermissions] = useState<UserPermissions>();
  const [groupPermissions, setGroupPermissions] = useState<UserPermissions>();
  const statusBar = useStatusBarState();
  const navigateTo = useNavigate();
  const { id } = useParams<{ id?: string }>();

  useEffect(() => {
    id && initializeState(Number(id));

    return () => {
      abortController && abortController.abort();
    };
  }, [id]);

  const initializeState = async (id: number) => {
    const abortController = resetAbortController();
    const permissions = await getUserPermissions(
      Administration.Lists.GlobalNavigationItems.InternalName,
      Administration.InternalName,
      abortController.signal,
    );
    if (!abortController.signal.aborted) {
      setPermissions(permissions);
      const canRead = permissions.schemaPermission && permissions.listPermissions.includes("read-all");
      !canRead && setErrorCode(403);
      if (canRead) {
        const [userPermissions, groupPermissions, result] = await Promise.all([
          getUserPermissions(
            Administration.Lists.Users.InternalName,
            Administration.InternalName,
            abortController.signal,
          ),
          getUserPermissions(
            Administration.Lists.Groups.InternalName,
            Administration.InternalName,
            abortController.signal,
          ),
          getGlobalNavigationItem(id, abortController.signal),
        ]);
        if (!abortController.signal.aborted) {
          result.error && setErrorCode(result.error.code);

          if (result.data) {
            setUserPermissions(userPermissions);
            setGroupPermissions(groupPermissions);
            setState(result.data);
            userPermissions?.listPermissions.includes("read-all") &&
              (await loadUsers(result.data.id, usersPagination, abortController));
            groupPermissions?.listPermissions.includes("read-all") &&
              (await loadGroups(result.data.id, groupsPagination, abortController));
          }
        }
      }
    }
  };

  const userFields = [UserFields.name, UserFields.email];
  const groupFields = [GroupFields.name, GroupFields.description];

  const [showUsersDialog, setShowUsersDialog] = useState(false);
  const usersDialogOptions: DialogOptions<UserFull> = {
    title: Administration.Lists.Users.Title,
    fields: [UserFields.name, UserFields.email, UserFields.department],
    searchItems: (value: string, abortController: AbortController) => searchUsers(value, abortController.signal),
    onItemSelected: async (user: UserFull) => {
      if (state) {
        const abortController = resetAbortController();
        await addUser(state, user, abortController.signal);
        await loadUsers(state.id, usersPagination, abortController);
      }
    },
    cancelClicked: () => setShowUsersDialog(false),
    isOpen: showUsersDialog,
  };

  const [showGroupsDialog, setShowGroupsDialog] = useState(false);
  const groupDialogOptions: DialogOptions<GroupFull> = {
    title: Administration.Lists.Groups.Title,
    fields: [GroupFields.name, GroupFields.description],
    searchItems: (value: string, abortController: AbortController) => searchGroups(value, abortController.signal),
    onItemSelected: async (group: GroupFull) => {
      if (state) {
        const abortController = resetAbortController();
        await addGroup(state, group, abortController.signal);
        await loadGroups(state.id, groupsPagination, abortController);
      }
    },
    cancelClicked: () => setShowGroupsDialog(false),
    isOpen: showGroupsDialog,
  };

  const onDelete = async () => {
    if (state && confirm(strings.confirmDeleteItem)) {
      const abortController = resetAbortController();
      const error = await deleteGlobalNavigationItem(state.id, abortController.signal);
      if (!abortController.signal.aborted) {
        error && statusBar.addError(getString(error));
        !error && navigateTo(getSourceFromUrl() ?? allItems);
      }
    }
  };
  const editAction: IAction = {
    title: strings.edit,
    icon: Edit,
    onClick: () => state && navigateTo(`${editForm}/${state.id}?${getCurrentUrlAsSource()}`),
    disabled: !permissions || !permissions.listPermissions.includes("edit-all"),
  };
  const closeAction: IAction = getCloseAction(navigateTo, allItems);
  const deleteAction: IAction = {
    title: strings.delete,
    icon: Bin,
    onClick: onDelete,
    disabled: !permissions || !permissions.listPermissions.includes("delete-all"),
  };

  const listIdGniUsers = "gni-users";
  const [users, setUsers] = useState<SearchResult<UserFull> | null>(null);

  const usersPagination = getPagination(listIdGniUsers) || {
    ...DefaultPagination,
  };

  const loadUsers = async (
    glonalNavigationItemId: number,
    pagination: Pagination,
    abortController: AbortController,
  ) => {
    const result = await getGlobalNavigationItemUsers(glonalNavigationItemId, pagination, abortController.signal);
    if (!abortController.signal.aborted) {
      result.error && statusBar.addError(getString(result.error));
      result.data && setUsers(result.data);
    }
  };

  const listIdGniGroups = "gni-groups";
  const [groups, setGroups] = useState<SearchResult<GroupFull> | null>(null);

  const groupsPagination = getPagination(listIdGniGroups) || {
    ...DefaultPagination,
  };

  const loadGroups = async (
    globalNavigationItemId: number,
    pagination: Pagination,
    abortController: AbortController,
  ) => {
    const result = await getGlobalNavigationItemGroups(globalNavigationItemId, pagination, abortController.signal);
    if (!abortController.signal.aborted) {
      result.error && statusBar.addError(getString(result.error));
      result.data && setGroups(result.data);
    }
  };

  const addGroupAction: IAction = {
    title: strings.add,
    icon: Add,
    onClick: () => setShowGroupsDialog(true),
    disabled:
      !groupPermissions?.listPermissions.includes("read-all") || !permissions?.listPermissions.includes("edit-all"),
  };

  const editGroupAction: IAction = {
    title: strings.edit,
    icon: Edit,
    onClick: () => state && navigateTo(`${editFormGroup}/${state.id}?${getCurrentUrlAsSource()}`),
    disabled: getSelectedItems(groups).length !== 1 || !groupPermissions?.listPermissions.includes("edit-all"),
  };

  const deleteGroupAction: IAction = {
    title: strings.remove,
    icon: Bin,
    onClick: async () => {
      if (state) {
        const selectedItems = getSelectedItems(groups);
        if (selectedItems.length > 0 && confirm(strings.confirmRemoveItems)) {
          const abortController = resetAbortController();
          await removeGroups(state, selectedItems, abortController.signal);
          await loadGroups(state.id, groupsPagination, abortController);
        }
      }
    },
    disabled: getSelectedItems(groups).length === 0 || !permissions?.listPermissions.includes("edit-all"),
  };

  const groupActions = [addGroupAction, editGroupAction, deleteGroupAction];

  const addUserAction: IAction = {
    title: strings.add,
    icon: Add,
    onClick: () => setShowUsersDialog(true),
    disabled:
      !userPermissions?.listPermissions.includes("read-all") || !permissions?.listPermissions.includes("edit-all"),
  };

  const removeUsersAction: IAction = {
    title: strings.remove,
    icon: Bin,
    onClick: async () => {
      if (state) {
        const selectedItems = getSelectedItems(users);
        if (selectedItems.length > 0 && confirm(strings.confirmRemoveItems)) {
          const abortController = resetAbortController();
          await removeUsers(state, selectedItems, abortController.signal);
          await loadUsers(state.id, usersPagination, abortController);
        }
      }
    },
    disabled: getSelectedItems(users).length === 0 || !permissions?.listPermissions.includes("edit-all"),
  };

  const actions = [addUserAction, removeUsersAction];

  useEffect(() => {
    if (state) {
      setListName(Administration.Lists.GlobalNavigationItems.Title);
      setPage(state.name);
    }
  }, [state]);

  return (
    <>
      {!errorCode && state && (
        <>
          <Title text={"Eintrag globales Menü"} />
          <Actions actions={[editAction, closeAction, deleteAction]} />
          <FormTable>
            <FormTr>
              <FormLabel>{Fields.name.title}</FormLabel>
              <FormData disp>{state?.name}</FormData>
            </FormTr>
            <FormTr>
              <FormLabel>{Fields.url.title}</FormLabel>
              <FormData disp>{state?.url}</FormData>
            </FormTr>
            <FormTr>
              <FormLabel>{Fields.order.title}</FormLabel>
              <FormData disp>{state?.order}</FormData>
            </FormTr>
            <FormTr>
              <FormLabel>{Fields.openNewTab.title}</FormLabel>
              <FormData disp>{formatBoolean(state?.openNewTab)}</FormData>
            </FormTr>
            <FormTr>
              <FormLabel>{Fields.parent.title}</FormLabel>
              <FormData disp>{state?.parent?.name}</FormData>
            </FormTr>
          </FormTable>
          <CreatedModifiedInfo info={state} />

          {usersDialogOptions.isOpen && (
            <SearchDialog
              isOpen={usersDialogOptions.isOpen}
              searchItems={usersDialogOptions.searchItems}
              onItemSelected={usersDialogOptions.onItemSelected}
              cancelClicked={() => setShowUsersDialog(false)}
              title={usersDialogOptions.title}
              fields={usersDialogOptions.fields}
            />
          )}
          <ReferencedItemsView
            itemId={state.id}
            id={listIdGniUsers}
            actions={actions}
            title={Administration.Lists.Users.Title}
            fields={userFields}
            allowSelectRows
            reload={(id, pagination) => {
              const abortController = resetAbortController();
              return loadUsers(id, pagination, abortController);
            }}
            searchResult={users}
            getFilters={getGlobalNavigationItemUsersFilters}
            pagination={usersPagination}
            updateItems={(items) =>
              users &&
              setUsers({
                ...users,
                items: items,
              })
            }
          />
          {groupDialogOptions.isOpen && (
            <SearchDialog
              isOpen={groupDialogOptions.isOpen}
              searchItems={groupDialogOptions.searchItems}
              onItemSelected={groupDialogOptions.onItemSelected}
              cancelClicked={() => setShowGroupsDialog(false)}
              title={groupDialogOptions.title}
              fields={groupDialogOptions.fields}
            />
          )}
          <ReferencedItemsView
            itemId={state.id}
            id={listIdGniGroups}
            actions={groupActions}
            title={Administration.Lists.Groups.Title}
            fields={groupFields}
            allowSelectRows
            reload={(id, pagination) => {
              const abortController = resetAbortController();
              return loadGroups(id, pagination, abortController);
            }}
            searchResult={groups}
            getFilters={getGlobalNavigationItemGroupsFilters}
            pagination={groupsPagination}
            updateItems={(items) =>
              groups &&
              setGroups({
                ...groups,
                items: items,
              })
            }
          />
        </>
      )}
      {errorCode && <Errors errorCode={errorCode} />}
    </>
  );
};

export default DispForm;
